import React, { useRef, useEffect } from 'react';

interface ProductProps {
    id: number;
    name: string;
    price: string;
    savings_price?: string;
    features?: string;
    trial?: boolean;
    product_name: string;
    tagline: string;
    savings: string;
    product_type?: string;
    stripe_plan: string;
    form_authenticity_token?: string;
    button?: boolean;
    user_signed_in?: boolean;
    auto_redirect?: boolean;
}

const Product: React.FC<ProductProps> = ({
    name,
    price,
    id,
    savings_price,
    features = '',
    trial = false,
    product_name,
    tagline,
    savings,
    product_type = 'plan',
    stripe_plan,
    form_authenticity_token,
    button = true,
    user_signed_in = false,
    auto_redirect = false
}) => {
    const formRef = useRef<HTMLFormElement>(null);
    // Some plans have no features string (Sentry DIRECTDENTAL-201).
    const plan_features: string[] = features ? features.split('|') : [];

    useEffect(() => {
        if (auto_redirect && formRef.current) {
            formRef.current.submit();
            formRef.current.dispatchEvent(new Event("submit"));
        }
    }, [auto_redirect]);

    const handleFormSubmit = (e: React.FormEvent) => {
        if (formRef.current && auto_redirect) {
            e.preventDefault();
            formRef.current.submit();
        }
    };

    // Determine if this is a boosted or full service plan
    const planClass = name.toLowerCase().includes('boosted') ? 'boosted-plan' :
                     name.toLowerCase().includes('full service') ? 'full-service-plan' : '';

    return (
        <div className={`plan-container ${planClass}`}>
            {auto_redirect && (
                <div className="auto-redirect-container">
                    <h2>Please wait while we process your request...</h2>
                    <div className="loading-spinner">
                        <i className="fa fa-spinner fa-spin"></i>
                    </div>
                    <form 
                        action="/payment_method" 
                        method="post" 
                        ref={formRef}
                        onSubmit={handleFormSubmit}
                    >
                        <input 
                            name="authenticity_token" 
                            value={form_authenticity_token} 
                            type="hidden" 
                        />
                        <input type="hidden" name="id" value={id} />
                        <input type="hidden" name="plan_id" value={stripe_plan} />
                    </form>
                </div>
            )}
            
            {!auto_redirect && (
                <div className="plan color-1 dropshadow">
                    <div className="plan-price">
                        <h3 className="comoTitle">
                            <strong style={{ color: 'white' }}>{name}</strong>
                        </h3>
                        {savings_price && (
                            <div className="savings-info">
                                <span className="original-price">${savings_price}</span>
                                <span className="savings-badge">{savings}% off</span>
                            </div>
                        )}
                        <div className="price-display">
                            <span className="plan-currency">$ </span>
                            <span className="value">{price}</span>
                        </div>
                        <p className="promo-yellow">
                            <small>{product_name}</small>
                        </p>
                        {tagline && (
                            <div className="product-tagline">
                                <small>{tagline}</small>
                            </div>
                        )}
                    </div>
                    
                    <div className="plan-features landing">
                        <ul>
                            {plan_features.map((feature, index) => (
                                <li key={index}>
                                    <i className="fa fa-check" aria-hidden="true"></i> 
                                    {feature.trim()}
                                </li>
                            ))}
                        </ul>
                        
                        {button && !user_signed_in && (
                            <a 
                                className="stripe-button-el" 
                                href={`/plans/${id}`}
                                aria-label={`Get started with ${name} plan`}
                            >
                                <span>
                                    Get Started
                                </span>
                            </a>
                        )}
                        
                        {button && user_signed_in && (
                            <form 
                                action="/payment_method" 
                                method="post" 
                                ref={formRef}
                                onSubmit={handleFormSubmit}
                            >
                                <input 
                                    name="authenticity_token" 
                                    value={form_authenticity_token} 
                                    type="hidden" 
                                />
                                <input type="hidden" name="id" value={id} />
                                <input type="hidden" name="plan_id" value={stripe_plan} />
                                <input 
                                    style={{ 
                                        width: '100%', 
                                        borderRadius: 0, 
                                        fontSize: 24 
                                    }} 
                                    className="stripe-button button" 
                                    type="submit" 
                                    value="Pay with Card" 
                                />
                            </form>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default Product;
